import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';

import type { PopupData, Service } from 'webapp-types';
import {
	BillingFeature,
	FakeModelIdPrefix,
	ModelName,
	PopupEvent,
	Theme,
} from 'webapp/enums/enums';
import { copyObject, equal, uniqueId } from 'webapp/utils/common';

import ScriptsFormPopupOptionsMathComponent from 'webapp/components/scripts/form/popup-options-math';
import ScriptsFormPopupOptionsMetricComponent from 'webapp/components/scripts/form/popup-options-metric';

import type DataSelect from 'webapp/models-custom/data-select';
import type { ExpressionDetailItem } from 'webapp/models-custom/script';
import Script from 'webapp/models-custom/script';
import ScriptItem, { ScriptItemGeneralType } from 'webapp/models-custom/script-item';
import type ScriptRecord from 'webapp/models/script';

import { Severity } from 'webapp/services/logger';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { TrackingOriginForMetricEdit } from 'webapp/services/model-manager/metrics';

export default class ModelManagerScriptsService extends ModelManagerForStoreService<
	ScriptRecord,
	Script
> {
	@service('billing') declare billingService: Service.Billing;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('account') declare accountService: Service.Account;
	@service('logger') declare loggerService: Service.Logger;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;

	get scriptModels(): Script[] {
		return this.createModelsFromRecordsArray(this.savedRecords);
	}

	createScriptItems(
		expression = '',
		expressionDetails: ExpressionDetailItem[] = [],
		isShownInList = false,
		skipPlaceholders = false,
		// @isShownInList - for showing code with mathematical signs in list of data calculations -
		// also to prevent getting all ms for every script item
	): { itemsPart1: ScriptItem[]; itemsPart2: ScriptItem[] } {
		let items: ScriptItem[] = [];

		const createItemsFromSignsOrPlaceholders = (chunk: string) => {
			const signItems: ScriptItem[] = [];
			chunk.split('').forEach((sign) => {
				// create from signs / placeholders
				const item = /([-+/*()]){1}/g.test(sign)
					? new ScriptItem(sign, '', isShownInList)
					: new ScriptItem(ScriptItemGeneralType.PLACEHOLDER, sign, isShownInList);
				signItems.push(item);
			});
			return signItems;
		};

		// remove whitespace
		expression = expression.split(' ').join('');

		const expressionIdRegEx = /({(?:(?!\}).)+})/g;
		let prevIndex = -1;
		let tmp;
		while ((tmp = expressionIdRegEx.exec(expression)) !== null) {
			// create signs that were between previous and this expression
			items.push(
				...createItemsFromSignsOrPlaceholders(expression.substring(prevIndex, tmp.index)),
			);

			// create from expression
			const expressionIdWithBrackets = tmp[0],
				expressionId = expressionIdWithBrackets.substring(
					1,
					expressionIdWithBrackets.length - 1,
				),
				expressionDetail = expressionDetails.find((exp) => exp.id === expressionId);

			if (expressionDetail) {
				items.push(
					new ScriptItem(
						expressionDetail?.type || 'missing',
						copyObject(expressionDetail), // create copy
						isShownInList,
					),
				);
			} else {
				this.loggerService.logError(
					'[createScriptItems] no expression',
					{
						expression,
						expressionId,
					},
					Severity.WARNING,
				);
			}

			// update prevIndex
			prevIndex = tmp.index + expressionIdWithBrackets.length;
		}
		// create signs that were after the last expression
		items.push(...createItemsFromSignsOrPlaceholders(expression.substring(prevIndex)));

		items = ScriptItem.cleanupItems(items);

		const addPlaceholders = !skipPlaceholders && !isShownInList;

		return this.splitAboveAndUnderLineItems(items, addPlaceholders);
	}

	addScriptItemPlaceholders(itemsPart1: ScriptItem[], itemsPart2: ScriptItem[]): void {
		// STARTING FORMULA: (A + B) / C
		// top side first
		let metricCount = itemsPart1.filter(
			(item) => !item.isBracket && !item.isMath && !item.isPlaceholder,
		).length;
		if (metricCount < 2) {
			const addFirst = metricCount === 0;
			if (addFirst) {
				itemsPart1.push(new ScriptItem(ScriptItemGeneralType.PLACEHOLDER, 'A'));
			}
			itemsPart1.push(new ScriptItem('+'));
			itemsPart1.push(new ScriptItem(ScriptItemGeneralType.PLACEHOLDER, 'B'));
		}

		// bottom side - if no metrics, clear everything and add a single placeholder
		metricCount = itemsPart2.filter(
			(item) => !item.isBracket && !item.isMath && !item.isPlaceholder,
		).length;
		if (metricCount === 0) {
			// remove all items (placeholders, brackets, operators)
			while (itemsPart2.length > 0) {
				const item = itemsPart2.pop();
				item?.cleanup();
			}
			itemsPart2.push(new ScriptItem(ScriptItemGeneralType.PLACEHOLDER, 'C'));
		}
	}

	private splitAboveAndUnderLineItems(
		items: ScriptItem[],
		addPlaceholders: boolean,
	): { itemsPart1: ScriptItem[]; itemsPart2: ScriptItem[] } {
		// split above-line and under-line items
		// find division(s) and check if they match criteria
		let bigDividerIndex = 0,
			itemsPart1 = items,
			itemsPart2: ScriptItem[] = [];

		items.forEach((item, k) => {
			if (item.type === '/') {
				const start = item.getLeftsideItemStartIndex(items),
					end = item.getRightsideItemEndIndex(items);

				if (start === 0 && end === items.length - 1) {
					bigDividerIndex = k;
				}
			}
		});

		if (bigDividerIndex) {
			itemsPart1 = items.slice(0, bigDividerIndex);
			itemsPart2 = items.slice(bigDividerIndex + 1);

			// remove surrounding brackets
			if (itemsPart1[0]?.type === '(') {
				itemsPart1.splice(0, 1);
				itemsPart1.splice(itemsPart1.length - 1, 1);
			}
			if (itemsPart2[0]?.type === '(') {
				itemsPart2.splice(0, 1);
				itemsPart2.splice(itemsPart2.length - 1, 1);
			}
		}

		if (addPlaceholders) {
			this.addScriptItemPlaceholders(itemsPart1, itemsPart2);
		}

		ScriptItem.setSelectedBrackets(itemsPart1);
		ScriptItem.setSelectedBrackets(itemsPart2);

		return {
			itemsPart1,
			itemsPart2,
		};
	}

	getExpressionDataFromItems(
		itemsPart1Val: ScriptItem[],
		itemsPart2Val: ScriptItem[],
		isShownInList = false, // see comment in createScriptItems
	): {
		expression: string | SafeString;
		expressionDetails: ExpressionDetailItem[];
	} {
		// make copies
		const itemsPart1 = Array.from(itemsPart1Val),
			itemsPart2 = Array.from(itemsPart2Val);
		let expression = '';

		const cleanItemsPart1 = ScriptItem.cleanupItems(itemsPart1),
			cleanItemsPart2 = ScriptItem.cleanupItems(itemsPart2);

		const topCode = cleanItemsPart1
			.map((item: ScriptItem) => {
				return item.getOutputInCode();
			})
			.join('');

		const bottomCode = cleanItemsPart2
			.map((item: ScriptItem) => {
				return item.getOutputInCode();
			})
			.join('');

		if (topCode) {
			if (bottomCode) {
				if (cleanItemsPart1.length > 1) {
					expression = `(${topCode})`;
				} else {
					expression = topCode;
				}

				expression += isShownInList
					? '<span class="sign"><ui-icon name="CalculationDivide" size="sm"></ui-icon></span>'
					: '/';
			} else {
				expression = topCode;
			}
		}
		if (bottomCode) {
			if (expression && cleanItemsPart2.length > 1) {
				expression += `(${bottomCode})`;
			} else {
				expression += bottomCode;
			}
		}

		const expressionDetails: ExpressionDetailItem[] = [];

		if (!isShownInList) {
			[...cleanItemsPart1, ...cleanItemsPart2].map((item) => {
				const exp = item.getExpressionDetails();
				if (exp) {
					expressionDetails.push(exp);
				}
			});
		}

		return {
			expression: isShownInList ? htmlSafe(expression) : expression,
			expressionDetails: copyObject(expressionDetails), // make a copy to keep script's array independent of items'
		};
	}

	openEditPopup(
		model: Script,
		origin: TrackingOriginForMetricEdit,
		afterSave?: PopupData.CustomMetric.Edit['afterSave'],
		openMetricDefinitionPopupOnInit?: boolean,
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (this.billingService.checkIfCan(BillingFeature.SCRIPTS, true)) {
			const popup = this.mmMetricsService.createCustomMetricEditPopup();

			const modelCopy = model.isNew ? model : this.createModel(model.model);

			popup.open({
				model: modelCopy,
				origin,
				afterSave,
				openMetricDefinitionPopupOnInit,
			});

			return popup;
		}
	}

	openEditPopupWithTransition(model: Script): PublicTransition {
		// The id, not the record: given a record, the route skips its model hook, which looks up the script.
		return this.navigationService.transitionToRoute('metrics.edit.calculated', model.idForSure);
	}

	openCreateNewPopup(
		origin: TrackingOriginForMetricEdit,
		afterSave?: PopupData.CustomMetric.Edit['afterSave'],
	) {
		if (this.billingService.checkIfCan(BillingFeature.CUSTOM_METRICS, true)) {
			const model = this.createNewModel();
			return this.openEditPopup(model, origin, afterSave);
		}
	}

	cloneScript(
		model: Script,
		origin: TrackingOriginForMetricEdit,
		afterSave?: PopupData.CustomMetric.Edit['afterSave'],
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const data = model.getDataForClone();

		const cloned = this.createNewModel(data);
		return this.openEditPopup(cloned, origin, afterSave);
	}

	deleteScript(model: Script) {
		if (this.accountService.handleDemo()) {
			return Promise.reject();
		}

		return new Promise((resolve, reject) => {
			const affirmativeFunc = () => {
				const layer = this.infolayerService.loading('Deleting custom metric...');

				resolve(null);

				model
					.delete()
					.then(
						() => {
							layer.ok('Custom metric has been deleted.');
						},
						(error) => {
							layer.close();
							throw error;
						},
					)
					.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
			};

			const popup = this.popupService.openConfirmPopup({
				onConfirm: affirmativeFunc,
				title: `Are you sure you want to delete this custom metric "${model.description}"?`,
				text: '',
				confirmAction: 'Delete',
				cancelAction: 'Cancel',
			});

			popup.on(PopupEvent.CLOSING, this, reject);
		});
	}

	openScriptMathOptions(attachTo: HTMLElement, data: PopupData.Script.OptionsMath) {
		// only skip when already open on this exact attachTo (then we want to close), otherwise all good bcos of allowMultipleInstances
		const alreadyOpenPopup = this.popupService.findContextByComponentAndAttachTo(
			ScriptsFormPopupOptionsMathComponent,
			attachTo,
		);
		if (alreadyOpenPopup) {
			return;
		}

		const popup = this.popupService.createContext(
			ScriptsFormPopupOptionsMathComponent,
			attachTo,
			{
				popupClass: 'popup-type-script-options-math',
				theme: Theme.DARK,
				neverFullwidth: true,
				allowMultipleInstances: true,
			},
		);

		popup.open(data);

		return popup;
	}

	openScriptMetricOptions(attachTo: HTMLElement, data: PopupData.Script.OptionsMetric) {
		// only skip when already open on this exact attachTo (then we want to close), otherwise all good bcos of allowMultipleInstances
		const alreadyOpenPopup = this.popupService.findContextByComponentAndAttachTo(
			ScriptsFormPopupOptionsMetricComponent,
			attachTo,
		);
		if (alreadyOpenPopup) {
			return;
		}

		const popup = this.popupService.createContext(
			ScriptsFormPopupOptionsMetricComponent,
			attachTo,
			{
				popupClass: 'popup-type-script-options-metric',
				theme: Theme.DARK,
				allowStacked: true, // for advanced modal
				allowMultipleInstances: true,
			},
		);

		popup.open(data);

		return popup;
	}

	updateDataSelectFromAnother(targetDataSelect: DataSelect, sourceDataSelect: DataSelect) {
		// this method is used for script item (updating from advanced/resetting changes)
		// handling only metricSetup and dateRange
		// changes in metrickey will cleanup filters and overrides
		// nothing else really is changed on data-select in scripts feature
		if (!equal(targetDataSelect.metricSetupData, sourceDataSelect.metricSetupData)) {
			targetDataSelect.updateMetricSetupData(sourceDataSelect.metricSetupData);
		}

		if (!targetDataSelect.dateRangeCM.isEqual(sourceDataSelect.dateRangeCM.formula)) {
			targetDataSelect.updateDateRange(sourceDataSelect.dateRangeCM);
		}
	}

	// ------- Brackets magic -------

	checkToRemoveExcessBrackets(
		expression: string,
		expressionDetails: ExpressionDetailItem[],
	): string | null {
		// cleaned expression returned. null means no change needed
		let bracketsChanged = false;
		const { itemsPart1, itemsPart2 } = this.createScriptItems(
			expression,
			expressionDetails,
			false,
			true,
		);
		[itemsPart1, itemsPart2].forEach((items) => {
			const outerBrackersOperator = this.getOuterBracketsOperator(items);
			if (outerBrackersOperator) {
				// double-checking
				const bracketItems = this.getBracketItemsForOperator(outerBrackersOperator, items);
				if (
					bracketItems &&
					bracketItems[0] === items[0] &&
					bracketItems[1] === items[items.length - 1]
				) {
					outerBrackersOperator.bracketChange(items, false);
					bracketsChanged = true;
				}
			}
		});
		if (bracketsChanged) {
			const { expression: newExpression } = this.getExpressionDataFromItems(
				itemsPart1,
				itemsPart2,
			);
			if (expression === newExpression) {
				return null;
			}
			return newExpression as string;
		}
		return null;
	}

	getBracketItemsForOperator(item: ScriptItem, items: ScriptItem[]) {
		return ScriptItem.getBracketItemsForOperator(item, items);
	}

	getOuterBracketsOperator(items: ScriptItem[]): ScriptItem | null {
		// if the first item is opening bracket, and the last item is MATCHING closing bracket
		// this method returns the operator that has control over these brackets

		if (items.length < 2) return null;
		if (items[0]?.type !== '(' || items[items.length - 1]?.type !== ')') return null;

		let bracketCounter = 0;
		let firstOperator: ScriptItem | null = null;

		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			if (item?.type === '(') bracketCounter++;
			else if (item?.type === ')') bracketCounter--;

			// If counter hits 0 before the last item, the first bracket closed early
			if (bracketCounter === 0 && i < items.length - 1) {
				return null;
			}

			// Capture the first operator at level 1 (inside the outer brackets)
			if (bracketCounter === 1 && item?.isMath && !firstOperator) {
				firstOperator = item;
			}
		}

		return firstOperator;
	}

	// ------ For fake blocks (preview) ------

	generateTemporaryFakeId() {
		return -1 * Number(`${FakeModelIdPrefix.DATA_CALCULATION}${uniqueId()}`);
	}

	isTemporaryFakeId(id: string | null = '') {
		const fakeModelPrefix = FakeModelIdPrefix.DATA_CALCULATION;
		const length = fakeModelPrefix.length;
		return !!id && id.substring(1, 1 + length) === fakeModelPrefix;
	}

	cleanupTemporaryFakeDataselects() {
		// remove fake / temporary dataselects without blockId
		// all, bcos we don't have good control which script-item were created and removed here
		// BEFORE CALLING THIS MAKE SURE THERE ARE NO MORE EDIT CUSTOM METRIC POPUPS OPEN THAT MIGHT STILL USE THESE TEMP
		this.mmDataSelectsService.savedRecords.forEach((ds) => {
			if (ds.id && +ds.id < 0 && !ds.blockId) {
				ds.unloadRecord();
			}
		});
	}

	// ------ Requests ------

	_scriptsRequested = false;
	_scriptsRequestPromise: Promise<void> | null = null;
	requestScripts(): Promise<void> {
		const variablePrefix = 'scripts';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.storeFindAll(ModelName.SCRIPT);
			},
		);
	}

	// ------ For-store ------

	modelName = ModelName.SCRIPT;
	override modelTitle = 'calculated metric';

	createModel(model: ScriptRecord): Script {
		return new Script({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/scripts': ModelManagerScriptsService;
	}
}
